/**
 * Wingman Service
 *
 * Custom client for the Wingman API with cookie-based auth support.
 * Replaces @lula-technologies-inc/wingman SDK.
 *
 * @example
 * ```typescript
 * import { WingmanClient, ConversationMessage } from "@/services/wingman";
 *
 * const client = new WingmanClient();
 * const conversation = await client.conversations.createConversation({
 *   xOrgId: orgId,
 *   createConversationRequest: { assistantId, type: "chat" },
 * });
 * ```
 */

// Client
export {WingmanClient, WingmanApiError, RESPONSE_BODY_MAX_LEN} from "./client";
export type {
   WingmanApiVersion,
   WingmanClientOptions,
   WingmanRequestFailure,
   WingmanRequestFailureHook,
   WingmanRequestFailureKind,
} from "./client";

// Routing
export {
   WINGMAN_V2_FLAG_KEY,
   resolveWingmanRouting,
   resolveConversationRouting,
   isAutoSelectableForConversation,
   getPinnedConversationRouting,
   discoverConversationRouting,
   pinConversationRouting,
   unpinConversationRouting,
   rankConversationHistory,
} from "./routing";
export type {ConversationHistoryRank, WingmanRouting} from "./routing";

export {
   WINGMAN_BFF_ROOT,
   signedInWingmanTransport,
   wingmanBffBaseUrl,
} from "./bff";

// Stream utility
export {WingmanEventStream} from "./stream";

// Stream event accumulator (pure reducer)
export {
   accumulateStreamEvent,
   appendContent,
   appendTextDelta,
   appendThinkingDelta,
   isMetadataEvent,
   isTransientEvent,
} from "./accumulator";

// Terminal status constants
export {GENERATION_TIMED_OUT_STATUS} from "./types";

// Types - Core
export type {Role, ConversationType, Chart} from "./types";

// Types - Enum (for backward compatibility)
export {WingmanRole} from "./types";

// Types - Charts
export type {PieChart, PieChartData, BarChart, BarChartData} from "./types";

// Types - Workspace building blocks
export type {
   WorkspaceContext,
   ScriptDraft,
   ScriptDraftDirection,
   ScriptDraftUseCase,
   ScriptDraftAction,
   ScriptSection,
   NavigationTarget,
   WorkflowBuild,
   WorkflowBuildPurpose,
} from "./types";

// Types - Document
export type {Document} from "./types";

// Types - Message Content (discriminated union)
export type {
   MessageContent,
   MessageContentMetadata,
   MessageContentText,
   MessageContentBinary,
   MessageContentGeneratedFile,
   MessageContentDocumentUpload,
   MessageContentFunctionCall,
   MessageContentFunctionResult,
   MessageContentBarChart,
   MessageContentPieChart,
   MessageContentScriptDraft,
   MessageContentBuildWorkflow,
   MessageContentNavigation,
   MessageContentNotification,
   MessageContentTokenUsage,
   MessageContentRecalledMemories,
   MessageContentThinking,
   MessageContentStatus,
   MessageContentCitation,
   MessageContentGenerationFailed,
   StreamingEvent,
   StreamingConversationMessage,
} from "./types";

// Types - Conversation Message
export type {ConversationMessage} from "./types";

// Types - Request/Response
export type {
   CreateConversationRequest,
   CreateConversationResponse,
   AppendConversationMessageRequest,
   ToolApprovalResponse,
   AppendConversationMessageV2Request,
   ConversationDetailsResponse,
   PageContext,
   PageEntityContext,
   EntitlementContext,
} from "./types";

// Types - Client method params
export type {
   CreateConversationParams,
   GetConversationParams,
   ListConversationMessagesParams,
   AppendConversationMessageParams,
   AppendConversationMessageV2Params,
   WingmanRequestOptions,
} from "./types";
